import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';

// Lazy load Layout component
const Layout = () => import('../components/Layout.vue');

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName: "login" */ '../views/Login.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: Layout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          redirect: '/dashboard',
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import(/* webpackChunkName: "dashboard" */ '../views/Dashboard.vue'),
        },
        {
          path: 'tenants',
          name: 'tenants',
          component: () => import(/* webpackChunkName: "tenants" */ '../views/Tenants.vue'),
        },
        {
          path: 'tenants/create',
          name: 'create-tenant',
          component: () =>
            import(/* webpackChunkName: "create-tenant" */ '../views/CreateTenant.vue'),
        },
        {
          path: 'tenants/:id',
          name: 'tenant-detail',
          component: () => import(/* webpackChunkName: "view-tenant" */ '../views/ViewTenant.vue'),
        },
        {
          path: 'billing',
          name: 'billing',
          component: () => import(/* webpackChunkName: "billing" */ '../views/Billing.vue'),
        },
        {
          path: 'billing/plans/:id/edit',
          name: 'edit-billing-plan',
          component: () =>
            import(/* webpackChunkName: "edit-billing-plan" */ '../views/EditBillingPlan.vue'),
        },
        {
          path: 'billing/invoices/:id',
          name: 'invoice-detail',
          component: () =>
            import(/* webpackChunkName: "invoice-detail" */ '../views/InvoiceDetails.vue'),
        },
        {
          path: 'platform-users',
          name: 'platform-users',
          component: () =>
            import(/* webpackChunkName: "platform-users" */ '../views/PlatformUsers.vue'),
        },
        {
          path: 'platform-users/create',
          name: 'create-platform-user',
          component: () =>
            import(
              /* webpackChunkName: "create-platform-user" */ '../views/CreatePlatformUser.vue'
            ),
        },
        {
          path: 'platform-users/:id',
          name: 'view-platform-user',
          component: () =>
            import(/* webpackChunkName: "view-platform-user" */ '../views/ViewPlatformUser.vue'),
        },
        {
          path: 'demo-requests',
          name: 'demo-requests',
          component: () =>
            import(/* webpackChunkName: "demo-requests" */ '../views/DemoRequests.vue'),
        },
        {
          path: 'features',
          name: 'features',
          component: () => import(/* webpackChunkName: "features" */ '../views/Features.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import(/* webpackChunkName: "settings" */ '../views/Settings.vue'),
        },
        {
          path: 'settings/permissions',
          name: 'permissions',
          component: () => import(/* webpackChunkName: "permissions" */ '../views/Permissions.vue'),
        },
        {
          path: 'settings/email-templates/:id/edit',
          name: 'edit-email-template',
          component: () =>
            import(/* webpackChunkName: "edit-email-template" */ '../views/EditEmailTemplate.vue'),
        },
        {
          path: 'holidays',
          name: 'holidays',
          component: () => import(/* webpackChunkName: "holidays" */ '../views/Holidays.vue'),
        },
      ],
    },
    // Now that each portal has its own subdomain, old path-prefixed URLs
    // (e.g. /admin/login) match nothing — redirect rather than render a blank
    // <router-view> inside the dark shell.
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
});

// Navigation guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' });
  } else {
    next();
  }
});

export default router;
